<template>
  <div style="width: 100%;">
    <div>
      <v-row>
        <v-col cols="12" md="2" class="left-panel">
          <div class="logo-container">
            <img src="../../assets/logo-cheque-intermittents-vector.svg" alt="Logo" class="logo" />
          </div>

          <div>
            <ul class="step-list">
              <li v-for="(step, index) in steps"
                  :key="index"
                  :class="{ active: index <= currentStep }">
                <div class="step-item">
                  <span class="step-number">{{ index + 1 }}</span>
                  <span class="step-label">{{ step }}</span>
                </div>
                <div class="step-line" v-if="index < steps.length - 1"></div>
              </li>
            </ul>
          </div>
        </v-col>

        <v-col cols="12" md="10" class="right-panel">
          <div class="right-panel-inner">
            <div class="main-content">
              <section-container v-if="displayCguStep"
                                 :dialog-open="true"
                                 :section="cguSection"
                                 @updated="acceptCgu"
                                 @close-dialog="next">
              </section-container>
              <component v-else
                         :is="sectionComponent"
                         ref="SECTION_DIALOG"
                         v-bind="currentProperties"
                         v-on="events"></component>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts">
  import { isMobile } from "@/style/StyleHelpers.ts";
  import { storeEtablissement, storeSession } from '@/generated/stores';
  import loaderButton from '@/framework/base/components/LoaderButton.vue';
  import { Sections } from '@/generated/classes/AppSchema.ts';
  import { type Component, defineComponent } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { AppCustoms, RessourceAppCustom } from "@/ressources/globalRessources.ts";
  import SectionContainer from "@/framework/base/components/SectionContainer.vue";
  import SectionCgu from "@/custom/sections/SectionCgu.vue";
  import SectionOnboardingAuthent from "@/components/Onboarding/SectionOnboardingAuthent.vue";
  import SectionOnboardingImport from "@/components/Onboarding/SectionOnboardingImport.vue";
  import SectionOnboardingAbo from "@/components/Onboarding/SectionOnboardingAbo.vue";
  import Synchro98, {
    Synchro98NbPaiesTypes,
    Synchro98ProjetGroupBys,
    Synchro98SalarieTypes,
    Synchro98ServiceTypes
  } from '@/generated/classes/Synchro98';
  import HubServices from '@/generated/hub/HubServices';
  import Section from "@/framework/base/classes/Section.ts";

  export default defineComponent({
    name: 'SectionRegister',
    components: {
      SectionContainer,
      loaderButton,
    },
    props: {
      code: {
        type: String
      },
      siteWeb: {
        type: String
      },
      identifiant: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        isMobile: isMobile(),
        synchro98: new Synchro98(),
        cguStepInTree: false,
        currentStep: 0,
        importInitialStep: 0,
        selectedProjetGroupBy: Synchro98ProjetGroupBys.NONE,
        selectedServiceType: Synchro98ServiceTypes.ASSISTED,
        selectedSalarieType: Synchro98SalarieTypes.INTERMITTENTS,
        selectedNbPaiesType: Synchro98NbPaiesTypes.MEDIUM,
        codeClient: 0,
        router: useRouter(),
        route: useRoute(),
      };
    },
    created(): void {
      const route = useRoute()
      const routeCode = route.params.code;
      const fallbackCode = Array.isArray(routeCode) ? routeCode[0] : routeCode;
      const code = this.code || fallbackCode;
      if (!code) {
        this.router.push({ name: "HomePage" });
        return;
      }
      Synchro98.getData(code).then((res) => {
        this.synchro98 = res;
        this.setSelectedChoicesFromSynchro();
        this.cguStepInTree = res.cguAcceptedAt == null;
        if (res.id == 0) {
          this.router.push({ name: "HomePage" });
        } else {
          storeSession.setToken(res.token, res.client98, res);
          HubServices.onSynchro98Progress(this.setProgress);
        }
      })
    },
    computed: {
      fromSection(): Sections {
        return Sections.CONNEXION;
      },
      steps(): string[] {
        const stepsList = [
          'Double authentification',
          'Transfert de vos données',
          'Choix d\'abonnement'
        ];
        if (this.displayCguInSteps) {
          stepsList.unshift('Conditions Générales d\'Utilisation');
        }
        return stepsList;
      },
      displayCguInSteps(): boolean {
        return this.cguStepInTree;
      },
      displayCguStep(): boolean {
        return this.displayCguInSteps && this.currentStep == 0;
      },
      cguSection(): Section {
        const section = new Section();
        section.component = SectionCgu;
        section.data = this.synchro98;
        return section;
      },
      onboardingStep(): number {
        return this.displayCguInSteps ? this.currentStep - 1 : this.currentStep;
      },
      sectionComponent(): Component | null {
        if (this.onboardingStep == 0) {
          return SectionOnboardingAuthent;
        } else if (this.onboardingStep == 1) {
          return SectionOnboardingImport;
        } else if (this.onboardingStep == 2) {
          return SectionOnboardingAbo;
        }
        return null;
      },
      currentProperties(): any {
        const properties: any = {
          "synchro98": this.synchro98,
          "selectedProjetGroupBy": this.selectedProjetGroupBy,
          "selectedServiceType": this.selectedServiceType,
          "selectedSalarieType": this.selectedSalarieType,
          "selectedNbPaiesType": this.selectedNbPaiesType
        };
        if (this.onboardingStep == 1) {
          properties.initialStep = this.importInitialStep;
        }
        return properties;
      },
      events(): Record<string, (...args: any[]) => void> {
        const event: any = {};
        event["next"] = () => this.next();
        event["previous"] = () => this.previous();
        event["failed"] = () => this.$emit("completed");
        event["updateProjetGroupBy"] = (value: Synchro98ProjetGroupBys) => this.setProjetGroupBy(value);
        event["updateServiceType"] = (value: Synchro98ServiceTypes) => this.setServiceType(value);
        event["updateSalarieType"] = (value: Synchro98SalarieTypes) => this.setSalarieType(value);
        event["updateNbPaiesType"] = (value: Synchro98NbPaiesTypes) => this.setNbPaiesType(value);
        return event;
      },
      subscriptionId(): number {
        const etablissementId = this.synchro98?.etablissementId ?? 0;
        if (etablissementId <= 0) {
          return 0;
        }
        return storeEtablissement.getEtablissement(etablissementId).subscriptionId;
      },
    },

    methods: {
      setSelectedChoicesFromSynchro(): void {
        if (this.synchro98.projetGroupBy !== Synchro98ProjetGroupBys.NONE) {
          this.selectedProjetGroupBy = this.synchro98.projetGroupBy;
        }
        if (this.synchro98.serviceType !== Synchro98ServiceTypes.NONE) {
          this.selectedServiceType = this.synchro98.serviceType;
        }
        if (this.synchro98.salarieType !== Synchro98SalarieTypes.NONE) {
          this.selectedSalarieType = this.synchro98.salarieType;
        }
        if (this.synchro98.nbPaiesType !== Synchro98NbPaiesTypes.NONE) {
          this.selectedNbPaiesType = this.synchro98.nbPaiesType;
        }
      },
      setProgress(synchro98: Synchro98) {
        this.synchro98 = synchro98;
      },
      setProjetGroupBy(value: Synchro98ProjetGroupBys): void {
        this.selectedProjetGroupBy = value;
        this.synchro98.projetGroupBy = value;
      },
      setServiceType(value: Synchro98ServiceTypes): void {
        this.selectedServiceType = value;
        this.synchro98.serviceType = value;
      },
      setSalarieType(value: Synchro98SalarieTypes): void {
        this.selectedSalarieType = value;
        this.synchro98.salarieType = value;
      },
      setNbPaiesType(value: Synchro98NbPaiesTypes): void {
        this.selectedNbPaiesType = value;
        this.synchro98.nbPaiesType = value;
      },
      acceptCgu(): void {
        if (this.synchro98.cguAcceptedAt == null) {
          Synchro98.acceptCgu(this.synchro98).then(() => {
            this.synchro98.cguAcceptedAt = new Date();
            this.next();
          });
        } else {
          this.next();
        }
      },
      next(): void {
        if (this.currentStep >= this.steps.length - 1) {
          this.$emit('completed');
          return;
        }
        if (this.onboardingStep == 0) {
          this.importInitialStep = 0;
        }
        if (this.onboardingStep == 1) {
          if (this.subscriptionId > 0) {
            Synchro98.setSubscription(this.synchro98).then((res) => {
              storeSession.clearData();
              storeSession.setUserData(res as any);
              this.$emit('completed');
            });
          } else {
            this.currentStep += 1;
          }
          return;
        }
        this.currentStep += 1;
      },
      previous(): void {
        if (this.currentStep === 0) {
          this.$emit('toggleForm');
        } else {
          if (this.onboardingStep == 2) {
            this.importInitialStep = 1;
          }
          this.currentStep -= 1;
        }
      },
    }
  })
</script>
<style>

  .subscribe-etab-identite .select-field {
    padding-top: 0 !important;
  }

  .left-panel {
    background-color: rgb(var(--v-theme-primary100));
    box-sizing: border-box;
    padding: 20px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .right-panel {
    box-sizing: border-box;
    padding: 30px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 100vh;
    background-color: rgb(var(--v-theme-grey97));
    position: relative;
  }

  .right-panel-inner {
    position: relative;
    height: 100%;
  }

  .main-content {
    flex-grow: 1;
    margin-top: 50px;
    padding-left: 80px;
    width: 800px;
  }

  .logo-container {
    margin-top: 50px;
    display: flex;
    justify-content: center;
  }

  .logo {
    object-fit: contain;
    width: 140px;
    height: 38px;
  }

  .step-list {
    margin-top: 100px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-width: 200px;
    margin-left: auto;
    margin-right: auto;
  }

    .step-list li {
      display: flex;
      align-items: start;
      flex-shrink: 0;
      position: relative;
      flex-direction: column;
    }

  .step-item {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
  }

  .step-number {
    background-color: rgb(var(--v-theme-grey95));
    border-radius: 8px;
    gap: 8px;
    padding: 8px;
    color: rgb(var(--v-theme-secondary50));
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
    font-weight: 500;
  }

  .step-label {
    font-size: 12px;
    color: rgb(var(--v-theme-secondary50));
    white-space: nowrap;
    font-weight: 400;
  }

  .step-list li.active .step-label {
    font-weight: 500;
    color: #171A1C;
  }

  .step-list li.active .step-number {
    font-weight: 500;
    color: rgb(var(--v-theme-grey10));
  }

  .step-line {
    width: 2px;
    height: 32px;
    background-color: rgb(var(--v-theme-grey80));
    margin-left: 11px;
  }

  .disabled-section {
    opacity: 0.3;
    pointer-events: none;
    user-select: none;
  }

  .gradient-ia {
    background: linear-gradient(to right, rgb(var(--v-theme-information10)), rgb(var(--v-theme-information50)));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: bold;
  }

  .textParamConnextion {
    padding-left: 80px;
    margin-bottom: 80px;
    font-weight: 400;
    font-size: 12px;
    color: rgb(var(--v-theme-secondary50));
  }
</style>
